import React, { useState, useMemo } from "react";
import { BookOpen, GraduationCap, ChevronDown, ChevronUp, ExternalLink, Share2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const buttonBase = "px-4 sm:px-5 py-2 md:py-3 font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm md:text-base";

const Schools = () => {
  const [openSchool, setOpenSchool] = useState(null);
  const [programType, setProgramType] = useState("nd");

  // Data
  const ndPrograms = useMemo(() => ({
    "SCHOOL OF AGRICULTURE AND AGRICULTURAL TECHNOLOGY": [
      "Agricultural Technology",
      "Horticulture and Landscape Technology"
    ],
    "SCHOOL OF APPLIED AND NATURAL SCIENCES": [
      "Tourism Management Technology",
      "Hospitality Management",
      "Health Information Technology",
      "Food Science Technology",
      "Nutrition and Dietetics",
      "Science Laboratory Technology",
      "Statistics"
    ],
    "SCHOOL OF BUSINESS AND MANAGEMENT": [
      "Business Administration and Management",
      "Marketing",
      "Public Administration",
      "Procurement and Supply Chain"
    ],
    "SCHOOL OF COMPUTING TECHNOLOGY": [
      "Computer Science"
    ],
    "SCHOOL OF COMMUNICATION AND MEDIA STUDIES": [
      "Mass Communication"
    ],
    "SCHOOL OF ENGINEERING TECHNOLOGY": [
      "Agricultural and Bio-Environmental Engineering Technology",
      "Chemical Engineering",
      "Civil Engineering",
      "Computer Engineering",
      "Electrical Engineering",
      "Mechanical Engineering",
      "Renewable Energy"
    ],
    "SCHOOL OF ENVIRONMENTAL STUDIES": [
      "Architectural Technology",
      "Building Technology",
      "Estate Management and Valuation",
      "Environmental Science and Management",
      "Quantity Surveying",
      "Surveying and Geo-Informatics",
      "Urban and Regional Planning"
    ],
    "SCHOOL OF FINANCIAL STUDIES": [
      "Accountancy",
      "Banking and Finance",
      "Taxation"
    ],
    "SCHOOL OF INFORMATION MANAGEMENT TECHNOLOGY": [
      "Health Information Management",
      "Library and Information Science",
      "Office Technology and Management"
    ]
  }), []);

  const hndPrograms = useMemo(() => ({
    "SCHOOL OF AGRICULTURE AND AGRICULTURAL SCIENCES": [
      "Agricultural Technology (Crop Production)",
      "Horticulture and Landscape Technology"
    ],
    "SCHOOL OF APPLIED AND NATURAL SCIENCES": [
      "Biology",
      "Biology/Microbiology",
      "Environmental Biology",
      "Microbiology",
      "Chemistry",
      "Biochemistry",
      "Biochemistry/Microbiology",
      "Chemistry/Biochemistry",
      "Physics/Electronics",
      "Hospitality Management",
      "Statistics",
      "Nutrition and Dietetics",
      "Tourism & Management Technology"
    ],
    "SCHOOL OF BUSINESS AND MANAGEMENT": [
      "Business Administration and Management",
      "Marketing",
      "Public Administration"
    ],
    "SCHOOL OF COMPUTING TECHNOLOGY": [
      "Artificial Intelligence Technology",
      "Cyber Security & Data Protection",
      "Networking & Cloud Computing",
      "Software & Web Development"
    ],
    "SCHOOL OF COMMUNICATION AND MEDIA STUDIES": [
      "Film & Media Studies",
      "Journalism & Media Studies",
      "Strategic Communication & Media Studies"
    ],
    "SCHOOL OF ENGINEERING TECHNOLOGY": [
      "Agricultural and Bio-Environmental Engineering Technology",
      "Chemical Engineering",
      "Civil Engineering",
      "Computer Engineering",
      "Electrical Engineering Technology",
      "Mechanical Engineering"
    ],
    "SCHOOL OF ENVIRONMENTAL STUDIES": [
      "Architectural Technology",
      "Building Technology",
      "Estate Management and Valuation",
      "Quantity Surveying",
      "Surveying and Geo-Informatics",
      "Urban and Regional Planning"
    ],
    "SCHOOL OF FINANCIAL STUDIES": [
      "Accountancy",
      "Banking and Finance"
    ],
    "SCHOOL OF INFORMATION MANAGEMENT TECHNOLOGY": [
      "Library and Information Science",
      "Office Technology and Management"
    ]
  }), []);

  const currentPrograms = programType === "nd" ? ndPrograms : hndPrograms;
  const totalPrograms = Object.values(currentPrograms).flat().length;
  const schoolsCount = Object.keys(currentPrograms).length;

  // Motion Variants
  const containerFade = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } };
  const listItem = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

  return (
    <div className="relative w-full min-h-screen bg-gradient-to-b from-sky-50 to-white py-12 sm:py-16 overflow-hidden">
      {/* Animated gradient glow - properly contained */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-0 right-0 w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 opacity-10 sm:opacity-15"
          style={{
            background: 'radial-gradient(circle at 30% 30%, rgba(59,130,246,0.12), transparent 70%)',
            filter: 'blur(40px)',
            borderRadius: '50%',
            animation: 'float 12s ease-in-out infinite'
          }}
        />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-20px, 20px) rotate(10deg); }
        }
      `}</style>

      <div className="relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        {/* Header */}
        <motion.header
          initial="hidden"
          animate="show"
          variants={containerFade}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-full mb-4 shadow-sm">
            <BookOpen className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" />
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 leading-tight px-4">
            Schools & Programs
          </h1>
          <p className="text-sm sm:text-base text-slate-600 max-w-3xl mx-auto px-4">
            Explore our National Diploma (ND) and Higher National Diploma (HND) programs — tailored for practical skills and industry readiness.
          </p>
        </motion.header>

        {/* Program switcher */}
        <motion.div
          className="flex justify-center mb-6 sm:mb-8 px-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
        >
          <div className="inline-flex rounded-lg bg-white shadow-sm ring-1 ring-slate-100 overflow-hidden w-full sm:w-auto">
            <motion.button
              onClick={() => setProgramType("nd")}
              aria-pressed={programType === "nd"}
              whileTap={{ scale: 0.98 }}
              className={`${buttonBase} flex-1 sm:flex-initial ${programType === "nd" ? "bg-blue-600 text-white" : "text-slate-700 bg-transparent hover:bg-slate-50"} flex items-center justify-center gap-2`}
            >
              <GraduationCap className="w-4 h-4 opacity-90" />
              <span className="hidden sm:inline">National Diploma (ND)</span>
              <span className="sm:hidden">ND</span>
            </motion.button>

            <motion.button
              onClick={() => setProgramType("hnd")}
              aria-pressed={programType === "hnd"}
              whileTap={{ scale: 0.98 }}
              className={`${buttonBase} flex-1 sm:flex-initial ${programType === "hnd" ? "bg-blue-600 text-white" : "text-slate-700 bg-transparent hover:bg-slate-50"} flex items-center justify-center gap-2`}
            >
              <BookOpen className="w-4 h-4 opacity-90" />
              <span className="hidden sm:inline">Higher National Diploma (HND)</span>
              <span className="sm:hidden">HND</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Stats / Program counter */}
        <motion.div
          className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 md:p-6 mb-6 sm:mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div className="flex-1">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold text-slate-900">
              {programType === "nd" ? "National Diploma Programs" : "Higher National Diploma Programs"}
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              Across <span className="font-medium text-slate-900">{schoolsCount}</span> schools — <span className="font-medium text-blue-600">{totalPrograms}</span> programs
            </p>
          </div>

          <div className="flex gap-2 sm:gap-3 w-full md:w-auto">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 md:flex-initial bg-blue-50 text-blue-700 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm md:text-base font-medium shadow-sm hover:shadow-md"
            >
              Filter
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 md:flex-initial flex items-center justify-center gap-2 px-3 sm:px-5 py-2 sm:py-3 rounded-lg text-xs sm:text-sm md:text-base font-semibold text-white shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600"
            >
              <Share2 className="w-4 h-4" />
              <span>Share</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Accordion of schools */}
        <motion.div
          className="space-y-3 sm:space-y-4"
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
        >
          {Object.entries(currentPrograms).map(([school, programs], idx) => (
            <motion.div
              key={school}
              className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 overflow-hidden"
              variants={listItem}
              whileHover={{ scale: 1.002 }}
              transition={{ type: "spring", stiffness: 210, damping: 22 }}
            >
              <button
                onClick={() => setOpenSchool(openSchool === idx ? null : idx)}
                aria-expanded={openSchool === idx}
                aria-controls={`school-${idx}`}
                className="w-full px-4 sm:px-5 md:px-6 py-3 sm:py-4 flex items-start justify-between gap-3 sm:gap-4 text-left hover:bg-slate-50 transition"
              >
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-slate-900 break-words">
                    {school}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 mt-1">
                    {programs.length} {programs.length === 1 ? "program" : "programs"}
                  </p>
                </div>

                <span className="mt-1 text-slate-400 flex-shrink-0">
                  {openSchool === idx ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </span>
              </button>

              {/* Animated panel */}
              <AnimatePresence initial={false}>
                {openSchool === idx && (
                  <motion.section
                    id={`school-${idx}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.42, ease: "easeOut" }}
                    className="px-4 sm:px-5 md:px-6 py-3 sm:py-4 bg-slate-50 border-t border-slate-100 overflow-hidden"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      {programs.map((program, pIdx) => (
                        <motion.div
                          key={program}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.36, delay: pIdx * 0.03 }}
                          className="bg-white p-3 sm:p-4 rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition transform hover:-translate-y-1"
                        >
                          <h4 className="font-semibold text-slate-900 mb-2 text-xs sm:text-sm md:text-base break-words">
                            {program}
                          </h4>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs md:text-sm text-blue-700 bg-blue-50 px-2 py-1 rounded-full font-medium">
                              {programType === "nd" ? "ND" : "HND"}
                            </span>
                            <button className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap">
                              <span>Learn more</span>
                              <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.section>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

        {/* Extra Info & CTA */}
        <motion.div 
          className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" 
          initial={{ opacity: 0, y: 6 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.45 }}
        >
          <motion.article 
            className="bg-blue-50 rounded-2xl p-4 sm:p-6" 
            whileHover={{ scale: 1.01 }}
          >
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3">
              National Diploma (ND)
            </h3>
            <ul className="text-slate-700 space-y-2 text-xs sm:text-sm">
              <li>• 2-year full-time program</li>
              <li>• Minimum 5 O'Level credits</li>
              <li>• UTME score recommended</li>
              <li>• Practical technical training & attachments</li>
            </ul>
          </motion.article>

          <motion.article 
            className="bg-emerald-50 rounded-2xl p-4 sm:p-6" 
            whileHover={{ scale: 1.01 }}
          >
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3">
              Higher National Diploma (HND)
            </h3>
            <ul className="text-slate-700 space-y-2 text-xs sm:text-sm">
              <li>• 2-year advanced program</li>
              <li>• Typically requires ND + industrial experience</li>
              <li>• Specialized & project-driven learning</li>
              <li>• Strong industry alignment</li>
            </ul>
          </motion.article>
        </motion.div>

        <motion.div 
          className="mt-8 sm:mt-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-5 sm:p-6 text-white text-center" 
          initial={{ opacity: 0, y: 8 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.45 }}
        >
          <h3 className="text-lg sm:text-xl font-bold mb-2">Ready to Apply?</h3>
          <p className="text-xs sm:text-sm md:text-base max-w-xl mx-auto mb-4 px-4">
            Applications for the 2025/2026 academic session are open. Join a learning community that's hands-on and industry-focused.
          </p>
          <a 
            href="https://portal.fedpolybida.edu.ng/" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <motion.button 
              whileHover={{ scale: 1.03 }} 
              whileTap={{ scale: 0.98 }} 
              className="bg-white text-blue-700 px-5 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold shadow text-sm sm:text-base"
            >
              Apply Now
            </motion.button>
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default Schools;